import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import type { Printable } from '../types/content';
type ImageCarouselProps = {
  items: Printable[];
  label: string;
};
export function ImageCarousel({
  items,
  label
}: ImageCarouselProps) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(items.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [items.length]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let best = Infinity;
      Array.from(track.children).forEach((child, i) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < best) {
          best = d;
          closest = i;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrow = 'absolute top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-sheet ring-1 ring-cream-line transition-[transform,opacity] duration-150 ease-out hover:scale-105 disabled:pointer-events-none disabled:opacity-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral sm:flex';
  return <div className="relative" role="region" aria-roledescription="carousel" aria-label={label} data-id="element-196" data-mp="2z7djsw5x8jpb">
      <ul ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[7.5%] py-6 sm:gap-6 sm:px-[15%]" data-id="element-197" data-mp="2u9v9p2g63yfq">
        {items.map((p, i) => <li key={p.id} aria-roledescription="slide" aria-label={`${i + 1} of ${items.length}`} className="w-[85%] shrink-0 snap-center sm:w-[70%]" data-id="element-198" data-mp="13637ge7ej1bg" data-mpi={i}>
            <figure className={`flex h-[200px] items-center justify-center rounded-2xl bg-white p-4 shadow-card ring-1 ring-cream-line transition-[transform,opacity] duration-200 ease-out sm:h-[300px] sm:p-8 lg:h-[360px] ${i === active ? 'scale-100 opacity-100' : 'scale-[0.96] opacity-60'}`} data-id="element-199" data-mp="1c2c81w7485z6">
              <img src={p.src} alt={p.alt} loading="lazy" className="h-auto max-h-full w-auto max-w-full" data-id="element-200" data-mp="1vrctvbip3zmb" />
            </figure>
          </li>)}
      </ul>

      <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous design" className={`${arrow} left-2`} data-id="element-201" data-mp="24t2vfa6akext">
        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-202" data-mp="3pcyaowtr1vzh" />
      </button>
      <button type="button" onClick={() => goTo(active + 1)} disabled={active === items.length - 1} aria-label="Next design" className={`${arrow} right-2`} data-id="element-203" data-mp="3kb8w577bj06n">
        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-204" data-mp="3bcnf5ghhvt7z" />
      </button>

      <div className="mt-2 flex justify-center gap-2" data-id="element-205" data-mp="bwqi8oup4qf4">
        {items.map((p, i) => <button key={p.id} type="button" onClick={() => goTo(i)} aria-label={`Show design ${i + 1}`} aria-current={i === active} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? 'w-7 bg-coral' : 'w-2.5 bg-navy/20 hover:bg-navy/40'}`} data-id="element-206" data-mp="7udeu73f94li" data-mpi={i} />)}
      </div>
    </div>;
}